// The cell of a filter field, by its label as shown on screen
function fieldByLabel(text) {
	const label = [...document.querySelectorAll('.MuiInputLabel-root')].find(l => l.textContent.trim() === text)
	return label?.closest('.MuiGrid-item') || null
}

function buttonByText(text) {
	return [...document.querySelectorAll('button')].find(b => b.textContent.trim() === text) || null
}

// One spotlight around several elements (the engine reads its box, scrolls it and may set its scroll margin),
// cut to the visible box of `clip` when the elements scroll inside it
export function spanOf(elements, clip) {
	const els = [elements].flat(2).filter(Boolean)
	if (!els.length) return null
	const rect = () => {
		const rs = els.map(e => e.getBoundingClientRect())
		let top = Math.min(...rs.map(r => r.top)), left = Math.min(...rs.map(r => r.left))
		let bottom = Math.max(...rs.map(r => r.bottom)), right = Math.max(...rs.map(r => r.right))
		if (clip) {
			const c = clip.getBoundingClientRect()
			top = Math.max(top, c.top); left = Math.max(left, c.left); bottom = Math.min(bottom, c.bottom); right = Math.min(right, c.right)
		}
		return { top, left, bottom, right, width: right - left, height: bottom - top }
	}
	const target = {
		// the engine may set a scroll margin on a tall target
		style: {},
		getBoundingClientRect: rect,
		scrollIntoView: ({ behavior, block } = {}) => {
			// bring the first element into its scrolling box (a wide table scrolls sideways), then place the whole span:
			// a tall one at the top (below the page header), a full-width one under the header so the card fits below it,
			// any other in the centre
			els[0].scrollIntoView({ block: 'nearest', inline: 'nearest' })
			const r = rect(), vw = window.innerWidth, vh = window.innerHeight
			const underHeader = parseInt(target.style.scrollMarginTop) || 96
			const cardBeside = r.right + 390 < vw || r.left - 390 > 0
			const top = block === 'start' || !cardBeside ? underHeader : (vh - r.height) / 2
			window.scrollBy({ top: r.top - top, behavior })
		},
	}
	return target
}

// The results table (real or demo): the data table whose header has Website and Price
function resultsTable() {
	for (const table of document.querySelectorAll('table')) {
		const heads = [...table.querySelectorAll('thead th')].map(th => th.textContent.trim())
		if (heads.includes('Website') && heads.includes('Price')) return table.closest('.MuiPaper-root')
	}
	return null
}

// A column of the results table: its header and the first rows' cells
function resultsColumn(label, rows = 4) {
	const paper = resultsTable()
	const ths = [...(paper?.querySelectorAll('thead th') || [])]
	const idx = ths.findIndex(th => th.textContent.trim() === label)
	if (idx < 0) return null
	const cells = [...paper.querySelectorAll('tbody tr')].slice(0, rows).map(tr => tr.children[idx])
	return spanOf([ths[idx], cells], ths[idx].closest('[class*="responsiveBase"]'))
}

function toolbarButton(icon) {
	return resultsTable()?.querySelector(`.MuiToolbar-root .fa-${icon}`)?.closest('button') || null
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	{
		type: 'intro',
		icon: 'magnifying-glass-chart',
		title: ES ? 'Bienvenido a Búsquedas en Tiempo Real' : 'Welcome to Real-Time Searches',
		description: ES
			? 'Lanza búsquedas de precios competitivos en más de 153 webs de alquiler de coches simultáneamente. Configura los parámetros de búsqueda, pulsa buscar y observa cómo los resultados llegan en directo con un mapa de calor de precios por colores.'
			: 'Launch competitive price searches across 153+ car rental websites simultaneously. Configure your search parameters, hit search, and watch results stream in live with a color-coded price heat map.',
	},
	{
		type: 'field',
		findTarget: () => {
			// the two options of the selector share one box
			let el = [...document.querySelectorAll('p, span')].find(e => e.textContent.trim() === 'Pick-up only')
			while (el && !el.textContent.includes('Pick-up & drop-off')) el = el.parentElement
			return el || null
		},
		icon: 'toggle-on',
		title: ES ? 'Alquileres de solo ida' : 'One-Way Rentals',
		description: ES
			? 'Elige «Pick-up & drop-off» cuando el cliente devuelva el coche en una ubicación diferente. Aparecerá un segundo selector de oficina para el lugar de devolución. Con «Pick-up only» se busca en una sola oficina.'
			: 'Choose "Pick-up & drop-off" when the customer returns the car at a different location. A second office selector will appear for the dropoff location. "Pick-up only" searches a single office.',
		example: ES
			? 'Ej. Recogida en Madrid → Devolución en Barcelona'
			: 'e.g. Pick up in Madrid → Drop off in Barcelona',
	},
	{
		type: 'field',
		findTarget: () => fieldByLabel('Pickup Offices'),
		icon: 'city',
		title: ES ? 'Oficina de recogida' : 'Pickup Office',
		description: ES
			? 'Busca y selecciona el lugar de recogida. Puedes buscar por código de aeropuerto (IATA), nombre de ciudad o nombre de oficina. Este campo es obligatorio.'
			: 'Search and select the pickup location. You can search by airport code (IATA), city name, or office name. This is a required field.',
		example: ES
			? 'Aeropuerto de Madrid Barajas - MAD'
			: 'Madrid Barajas Airport - MAD',
	},
	{
		type: 'field',
		findTarget: () => fieldByLabel('Sites'),
		icon: 'globe-pointer',
		title: ES ? 'Webs a comparar' : 'Websites to Compare',
		description: ES
			? 'Selecciona qué webs de alquiler y metabuscadores consultar. Se permite selección múltiple: elige competidores específicos o selecciona todos para un análisis completo del mercado.'
			: 'Select which rental websites and metasearch engines to query. Multi-select supported — choose specific competitors or select all for a full market scan.',
		example: 'Kayak, Rentalcars, Sixt, Europcar, Booking...',
	},
	{
		type: 'field',
		findTarget: () => spanOf([fieldByLabel('Pickup time'), fieldByLabel('Dropoff time')]),
		icon: 'clock-rotate-left',
		title: ES ? 'Hora de recogida y devolución' : 'Pickup & Dropoff Time',
		description: ES
			? 'Establece las horas de recogida y devolución. Algunos proveedores aplican recargos en horarios de madrugada o noche, especialmente en aeropuertos.'
			: 'Set pickup and return hours. Some providers apply surcharges for early morning or late night times, especially at airports.',
		example: ES
			? 'Recogida 10:00 — Devolución 10:00'
			: 'Pickup 10:00 — Dropoff 10:00',
	},
	{
		type: 'field',
		findTarget: () => fieldByLabel('Rental dates'),
		icon: 'calendar-days',
		title: ES ? 'Fechas de alquiler' : 'Rental Dates',
		description: ES
			? 'Elige una o varias fechas futuras de recogida. Seleccionar varias fechas te permite detectar tendencias de precios en distintos periodos. Solo se pueden seleccionar fechas futuras.'
			: 'Choose one or more future pickup dates. Multiple dates let you spot pricing trends across different travel periods. Only future dates are selectable.',
		example: ES
			? '15 Mar, 22 Mar, 1 Abr'
			: 'Mar 15, Mar 22, Apr 1',
	},
	{
		type: 'field',
		findTarget: () => fieldByLabel('Rental length'),
		icon: 'calendar-clock',
		title: ES ? 'Duración del alquiler (LOR)' : 'Length of Rental (LOR)',
		description: ES
			? 'Añade duraciones de alquiler en días. Cada combinación de Fecha × LOR crea una tarea de búsqueda. Máximo 10 combinaciones por búsqueda para mantener la rapidez.'
			: 'Add rental durations in days. Each Date × LOR combination creates one search task. Maximum 10 combinations per search to keep it fast.',
		example: ES
			? '3 días, 5 días, 7 días → 3 fechas × 3 LOR = 9 tareas'
			: '3 days, 5 days, 7 days → 3 dates × 3 LORs = 9 tasks',
	},
	{
		type: 'field',
		findTarget: () => fieldByLabel('Currency'),
		icon: 'money-bill-1-wave',
		title: ES ? 'Moneda' : 'Currency',
		description: ES
			? 'Normaliza todos los precios a una única moneda para una comparación justa entre proveedores y mercados.'
			: 'Normalize all prices to a single currency for fair, apples-to-apples comparison across providers and markets.',
		example: ES
			? 'EUR (por defecto), USD, GBP'
			: 'EUR (default), USD, GBP',
	},
	{
		type: 'field',
		findTarget: () => spanOf([buttonByText('Stop Search'), buttonByText('Search')]),
		icon: 'magnifying-glass',
		title: ES ? 'Lanzar búsqueda' : 'Launch Search',
		description: ES
			? 'Pulsa «Search» para enviar consultas en paralelo a todas las webs seleccionadas. Los resultados llegan en directo — verás una barra de progreso con las tareas completadas. Con «Stop Search» detienes una búsqueda en curso.'
			: 'Click "Search" to dispatch parallel queries to all selected websites. Results stream in live — you\'ll see a progress bar tracking completed tasks. "Stop Search" stops a search mid-flight.',
	},
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-realtime-errors"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-realtime-errors"]'),
		icon: 'triangle-exclamation',
		title: ES ? 'Búsquedas sin precios' : 'Searches Without Prices',
		description: ES
			? 'Las búsquedas que no devolvieron precios se resumen en una línea: cuántas, de qué tipo (Not available, Error data, Bad config) y en cuántas webs y fechas. «Show detail» las lista por web y fecha de recogida; pulsa una duración con captura para abrirla.'
			: 'Searches that came back without prices are summed up in one line: how many, of which kind (Not available, Error data, Bad config), and on how many sites and dates. "Show detail" lists them per site and pickup date; click a rental length with a screenshot to open it.',
	},
	{
		type: 'field',
		findTarget: () => {
			const paper = resultsTable()
			if (!paper) return null
			// the title, the toolbar, the header and the first rows: as many as fit in about 340px (car models make rows tall)
			const toolbar = paper.querySelector('.MuiToolbar-root')
			const rows = [...paper.querySelectorAll('tbody tr')].slice(0, 4)
			const top = toolbar.getBoundingClientRect().top
			const last = rows.filter((r, i) => i === 0 || r.getBoundingClientRect().bottom - top <= 340).pop()
			return spanOf([toolbar, last], paper)
		},
		icon: 'table-cells',
		title: ES ? 'Tabla de resultados' : 'Results Table',
		description: ES
			? 'Los resultados de la búsqueda aparecen aquí ordenados por precio. Cada fila muestra la web, fecha de recogida, LOR, modelo de coche, código ACRISS, transmisión, categoría, kilometraje, proveedor, método de pago y precio. Los precios están codificados por colores con un mapa de calor.'
			: 'Search results appear here sorted by price. Each row shows the website, pickup date, LOR, car model, ACRISS code, transmission, category, mileage, provider, payment method, and price. Prices are color-coded with a heat map.',
		example: ES
			? 'Verde = tercio más barato | Naranja = rango medio | Rojo = tercio más caro'
			: 'Green = cheapest third | Orange = mid range | Red = most expensive third',
	},
	{
		type: 'field',
		findTarget: () => resultsColumn('Website'),
		skipIf: () => !resultsTable(),
		icon: 'earth-americas',
		title: ES ? 'Columna de web' : 'Website Column',
		description: ES
			? 'Muestra el nombre de la web de alquiler con su logotipo y bandera del país. Cada fila incluye iconos de acción rápida: un enlace para abrir el resultado original en la web y un botón de captura de pantalla para ver la página capturada.'
			: 'Shows the rental website name with its logo and country flag. Each row includes quick-action icons: a link to open the original search result on the website, and a screenshot button to view the captured page.',
	},
	{
		type: 'field',
		findTarget: () => resultsColumn('Price', 6),
		skipIf: () => !resultsColumn('Price'),
		icon: 'fire-flame-curved',
		title: ES ? 'Mapa de calor de precios' : 'Price Heat Map',
		description: ES
			? 'Los precios se ordenan y dividen en tercios. El tercio más barato es verde, el rango medio es naranja y el tercio más caro es rojo. La intensidad del color aumenta hacia los extremos, facilitando identificar las mejores y peores ofertas de un vistazo.'
			: 'Prices are sorted and divided into thirds. The cheapest third is green, the middle range is orange, and the most expensive third is red. Color intensity increases towards the extremes, making it easy to spot the best and worst deals at a glance.',
	},
	{
		type: 'field',
		findTarget: () => resultsTable()?.querySelector('[data-testid="Filter Table-iconButton"]') || null,
		skipIf: () => !resultsTable()?.querySelector('[data-testid="Filter Table-iconButton"]'),
		icon: 'filter',
		title: ES ? 'Filtros por columna' : 'Column Filters',
		description: ES
			? 'Cada columna tiene su propio filtro para refinar resultados. El botón del embudo abre el panel de filtros: web, DOR, LOR, ACRISS, transmisión, categoría, kilometraje, proveedor, método de pago y las columnas ocultas. El modelo de coche permite búsqueda por texto libre.'
			: 'Each column has its own filter to refine results. The funnel button opens the filter panel: website, DOR, LOR, ACRISS, transmission, category, mileage, provider, payment method, and the hidden columns. Car model supports free-text search.',
		example: ES
			? 'Ej. Categoría: Economy | Transmisión: Automatic | Proveedor: Europcar'
			: 'e.g. Category: Economy | Transmission: Automatic | Provider: Europcar',
	},
	{
		type: 'field',
		findTarget: () => resultsTable()?.querySelector('[data-testid="View Columns-iconButton"]') || null,
		skipIf: () => !resultsTable()?.querySelector('[data-testid="View Columns-iconButton"]'),
		icon: 'table-columns',
		title: ES ? 'Columnas ocultas' : 'Hidden Columns',
		description: ES
			? 'La tabla tiene columnas adicionales ocultas por defecto: asientos, puertas, rating, posición, en terminal, oferta especial, maletas grandes/pequeñas, proveedor web, kilometraje incluido, política de combustible, seguro y seguro de la web. Actívalas desde el botón de columnas en la barra de herramientas.'
			: 'The table has additional columns hidden by default: seats, doors, rating, position, in terminal, special offer, big/small suitcases, web supplier, mileage included, fuel policy, insurance, and web insurance. Enable them from the columns button in the toolbar.',
	},
	{
		type: 'field',
		findTarget: () => spanOf(['tally', 'calendar-days', 'handshake', 'star', 'chart-line'].map(toolbarButton)),
		skipIf: () => !toolbarButton('tally'),
		icon: 'chart-mixed',
		title: ES ? 'Acceso directo a analizadores' : 'Analyzer Quick Access',
		description: ES
			? 'Desde la barra de herramientas puedes abrir directamente los analizadores con los datos de esta búsqueda precargados: LOR Analyzer, DOR Analyzer, Brokers Analyzer, Rating Analyzer y Direct Site Analyzer. Cada uno aparece según los permisos de tu cuenta.'
			: 'From the toolbar you can open analyzers directly with this search\'s data preloaded: LOR Analyzer, DOR Analyzer, Brokers Analyzer, Rating Analyzer, and Direct Site Analyzer. Each one appears based on your account permissions.',
	},
	{
		type: 'field',
		findTarget: () => {
			const excel = [...(resultsTable()?.querySelectorAll('.MuiToolbar-root .fa-file-excel') || [])].map(i => i.closest('button'))
			return spanOf([excel, toolbarButton('cloud-arrow-down'), toolbarButton('file-arrow-down')])
		},
		skipIf: () => !toolbarButton('cloud-arrow-down') && !toolbarButton('file-arrow-down'),
		icon: 'file-export',
		title: ES ? 'Generar y exportar búsqueda' : 'Generate & Export Search',
		description: ES
			? 'Dos acciones de exportación disponibles: «Generate Search» genera el informe Excel de la búsqueda con todos sus resultados y lo abre. «Export Search» genera el fichero de exportación de los resultados. También puedes descargar coincidencias de categorías y ACRISS personalizados como Excel.'
			: 'Two export actions available: "Generate Search" builds the Excel report of the search with all its results and opens it. "Export Search" produces the export file of the results. You can also download custom category and ACRISS matches as Excel files.',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo listo!' : "You're All Set!",
		description: ES
			? 'Cada búsqueda genera una URL única que puedes compartir. Encuentra búsquedas anteriores en «Historical», o usa la barra de herramientas para acceder a los Analizadores y obtener análisis competitivos más profundos como comparaciones de Sitio Directo o Brokers.'
			: 'Every search generates a unique shareable URL. Find past searches in "Historical", or use the toolbar to jump into Analyzers for deeper competitive insights like Direct Site or Broker comparisons.',
	},
]
